import { For, Show, createEffect, createMemo, createSignal, onCleanup, type Accessor } from 'solid-js';
import '@styles/Insights.css';
import DropDownButton from '@components/buttons/DropDownButton';
import {
  DEFAULT_HOME_BOAT,
  ensureDefaultHomeBoat,
  hasEnoughFleetBoats,
  homeBoatOptions,
  resolveHomeBoatName,
  setHomeBoatName,
} from '../../insights/homeBoat';
import { selectedEvents, selectedGroupKeys } from '../../store/selectionStore';
import type { Finding, ShowMeAction } from '../../insights/types';
import InsightCard from './InsightCard';

export interface InsightPanelProps {
  /** Area label used in empty-state copy, e.g. "start". */
  areaLabel?: string;
  findings: Accessor<Finding[]> | Finding[];
  /** Number of boats in the comparison set (for insufficient-fleet messaging). */
  fleetSize?: Accessor<number> | number;
  onShowMe?: (action: ShowMeAction) => void;
  /** Start expanded. Default closed. */
  defaultOpen?: boolean;
  /**
   * When set, replaces the findings list. Used when the home boat is not in the
   * current race, so day-trend cards from other races are not shown.
   */
  statusMessage?: Accessor<string> | string;
}

/** True when Show-me (or equivalent) has anything highlighted — teams and/or maneuver event IDs. */
function hasInsightSelection(): boolean {
  return selectedGroupKeys().length > 0 || selectedEvents().length > 0;
}

export default function InsightPanel(props: InsightPanelProps) {
  const [open, setOpen] = createSignal(props.defaultOpen === true);
  /** Finding id whose Show-me selection is currently applied. */
  const [activeFindingId, setActiveFindingId] = createSignal<string | null>(null);
  /** Row id inside a consolidated card, when Show me was a single channel. */
  const [activePointId, setActivePointId] = createSignal<string | null>(null);
  /** Tracks whether a selection was present so we only clear the tile when selection goes away. */
  let hadInsightSelection = false;

  // Seed GER once when the store is still empty so the page is useful on first load.
  createEffect(() => {
    homeBoatOptions();
    void ensureDefaultHomeBoat();
  });

  /**
   * Drop the active-card mark when the user clears the selection (banner / X / row toggle).
   * Watches group keys (Prestart / Race Summary) and event IDs (Fleet Maneuvers).
   */
  createEffect(() => {
    const hasSel = hasInsightSelection();
    if (hasSel) {
      hadInsightSelection = true;
      return;
    }
    if (hadInsightSelection) {
      hadInsightSelection = false;
      setActiveFindingId(null);
      setActivePointId(null);
    }
  });

  createEffect(() => {
    const clearActive = () => {
      hadInsightSelection = false;
      setActiveFindingId(null);
      setActivePointId(null);
    };
    window.addEventListener('selection-banner-cleared', clearActive);
    onCleanup(() => window.removeEventListener('selection-banner-cleared', clearActive));
  });

  const homeBoat = createMemo(() => resolveHomeBoatName());
  const boatOptions = createMemo(() => homeBoatOptions());

  const findingsList = createMemo((): Finding[] => {
    const f = props.findings;
    return typeof f === 'function' ? f() : f;
  });

  const lossCount = createMemo(
    () => findingsList().filter((f) => f.severity === 'loss').length
  );

  const handleShowMe = (finding: Finding, action: ShowMeAction, pointId?: string) => {
    // Highlight the tile immediately; parent may apply selection sync (Prestart) or on
    // setTimeout(0) (Fleet Maneuvers — waits out selectionStore's clear guard).
    setActiveFindingId(finding.id);
    setActivePointId(pointId ?? null);
    props.onShowMe?.(action);
    setTimeout(() => {
      if (hasInsightSelection()) {
        hadInsightSelection = true;
        setActiveFindingId(finding.id);
        setActivePointId(pointId ?? null);
      } else {
        setActiveFindingId(null);
        setActivePointId(null);
      }
    }, 0);
  };

  const fleetSize = createMemo((): number => {
    const n = props.fleetSize;
    if (n == null) {
      return 0;
    }
    return typeof n === 'function' ? n() : n;
  });

  const title = createMemo(() => {
    const boat = homeBoat();
    return boat ? `What this means for ${boat}` : 'What this means for your boat';
  });

  const statusMessage = createMemo((): string => {
    const message = props.statusMessage;
    if (message == null) {return '';}
    return typeof message === 'function' ? message() : message;
  });

  const emptyMessage = createMemo(() => {
    const status = statusMessage();
    if (status) {return status;}
    if (!homeBoat()) {
      return `No home boat in this fleet (expected ${DEFAULT_HOME_BOAT}). Pick a boat above.`;
    }
    if (fleetSize() > 0 && !hasEnoughFleetBoats(fleetSize())) {
      return 'Need at least 3 boats in the table to compare against leaders.';
    }
    if (findingsList().length === 0) {
      const area = props.areaLabel ?? 'this';
      return `No meaningful gaps vs the leaders on ${area} for the current selection.`;
    }
    return '';
  });

  return (
    <section
      class={`insight-panel${open() ? ' insight-panel--open' : ''}`}
      data-insight-panel
    >
      <div class="insight-panel__header">
        <button
          type="button"
          class="insight-panel__toggle"
          aria-expanded={open()}
          onClick={() => setOpen((v) => !v)}
        >
          <span class="insight-panel__chevron" aria-hidden="true">
            {open() ? '▾' : '▸'}
          </span>
          <h3 class="insight-panel__title">{title()}</h3>
          <Show when={findingsList().length > 0 && !statusMessage()}>
            <span
              class={`insight-panel__count${lossCount() > 0 ? ' insight-panel__count--loss' : ''}`}
              title={`${findingsList().length} findings (${lossCount()} losses)`}
            >
              {findingsList().length}
            </span>
          </Show>
        </button>
        <div class="insight-panel__controls">
          <span class="insight-panel__boat-label">Home boat</span>
          <DropDownButton
            options={boatOptions()}
            defaultText={homeBoat() || DEFAULT_HOME_BOAT}
            size="small"
            handleSelection={(item) => {
              void setHomeBoatName(item);
            }}
          />
        </div>
      </div>
      <Show when={open()}>
        <div class="insight-panel__body">
          <Show
            when={findingsList().length > 0 && !!homeBoat() && !statusMessage()}
            fallback={<p class="insight-panel__empty">{emptyMessage()}</p>}
          >
            <For each={findingsList()}>
              {(finding) => (
                <InsightCard
                  finding={finding}
                  active={activeFindingId() === finding.id}
                  activePointId={activeFindingId() === finding.id ? activePointId() : null}
                  onShowMe={
                    props.onShowMe
                      ? (action) => handleShowMe(finding, action)
                      : undefined
                  }
                  onShowPoint={
                    props.onShowMe
                      ? (point) => {
                          if (point.showMe) {handleShowMe(finding, point.showMe, point.id);}
                        }
                      : undefined
                  }
                />
              )}
            </For>
          </Show>
        </div>
      </Show>
    </section>
  );
}
